import { z } from '@hono/zod-openapi'
import { LocaleCodeSchema } from './localization'

export const TranslationCategorySchema = z.enum(['general', 'education', 'legal', 'medical']).openapi({
  example: 'legal',
})

export const TranslationToneSchema = z.enum(['formal', 'neutral', 'parent-friendly']).openapi({
  example: 'parent-friendly',
})

export const TranslationOptionsSchema = z.object({
  glossary: z.array(z.string()).optional().openapi({ description: 'Glossary IDs to apply' }),
  useMemory: z.boolean().openapi({ example: true }),
  category: TranslationCategorySchema.optional(),
  preserveFormatting: z.boolean().openapi({ example: true }),
  tone: TranslationToneSchema.optional(),
}).openapi('TranslationOptions')

export const TranslationResultSchema = z.object({
  translatedText: z.string().openapi({ example: 'Programa de Educación Individualizada' }),
  detectedSourceLanguage: LocaleCodeSchema.optional(),
  provider: z.string().openapi({ example: 'azure' }),
  confidence: z.number().min(0).max(1).openapi({ example: 0.98 }),
  processingTimeMs: z.number().min(0).openapi({ example: 342 }),
  glossaryApplied: z.boolean().openapi({ example: true }),
  memoryMatched: z.boolean().openapi({ example: false }),
  memoryEntryId: z.string().optional(),
}).openapi('TranslationResult')

export const ProviderHealthSchema = z.object({
  status: z.enum(['healthy', 'degraded', 'down']).openapi({ example: 'healthy' }),
  latencyMs: z.number().min(0).openapi({ example: 120 }),
  remainingQuota: z.number().min(0).openapi({ example: 850000 }),
  rateLimitRemaining: z.number().min(0).openapi({ example: 4500 }),
  lastChecked: z.string().datetime().openapi({ example: '2026-07-15T12:00:00Z' }),
}).openapi('ProviderHealth')

export const TranslationStatusSchema = z.enum([
  'pending', 'machine-translated', 'human-review', 'approved', 'rejected', 'failed',
]).openapi({ example: 'pending' })

export const TranslationPrioritySchema = z.enum(['low', 'normal', 'high', 'urgent']).openapi({ example: 'normal' })

export const TranslationWorkflowSchema = z.enum(['auto', 'human-review-required']).openapi({ example: 'human-review-required' })

export const TranslationRequestSchema = z.object({
  id: z.string().min(1).max(64).openapi({ example: 'tr-1712345678900-1' }),
  documentId: z.string().min(1).max(64).openapi({ example: 'doc-a1b2c3d4' }),
  documentVersionId: z.string().min(1).max(64).openapi({ example: 'ver-e5f6g7h8' }),
  sourceLocale: LocaleCodeSchema.openapi({ example: 'en-US' }),
  targetLocale: LocaleCodeSchema.openapi({ example: 'es-US' }),
  priority: TranslationPrioritySchema,
  status: TranslationStatusSchema,
  provider: z.string().optional().openapi({ example: 'azure' }),
  machineTranslatedAt: z.string().datetime().optional(),
  humanReviewer: z.string().optional(),
  humanReviewedAt: z.string().datetime().optional(),
  approvedAt: z.string().datetime().optional(),
  workflow: TranslationWorkflowSchema,
}).openapi('TranslationRequest')

export const TranslationJobSchema = z.object({
  id: z.string().min(1).max(64).openapi({ example: 'job-1712345678900-1' }),
  requestId: z.string().min(1).max(64),
  texts: z.array(z.string()).openapi({ example: ['Hello world'] }),
  sourceLocale: LocaleCodeSchema,
  targetLocale: LocaleCodeSchema,
  options: TranslationOptionsSchema,
  status: z.enum(['queued', 'processing', 'completed', 'failed']).openapi({ example: 'queued' }),
  createdAt: z.string().datetime(),
  startedAt: z.string().datetime().optional(),
  completedAt: z.string().datetime().optional(),
  error: z.string().optional(),
  results: z.array(TranslationResultSchema).optional(),
}).openapi('TranslationJob')

export const TranslationMemoryEntrySchema = z.object({
  id: z.string().min(1).max(64).openapi({ example: 'tme-001' }),
  sourceText: z.string().min(1).openapi({ example: 'Individualized Education Program' }),
  sourceLocale: LocaleCodeSchema,
  targetText: z.string().min(1).openapi({ example: 'Programa de Educación Individualizada' }),
  targetLocale: LocaleCodeSchema,
  domain: z.string().openapi({ example: 'sped' }),
  context: z.string().optional(),
  approvedBy: z.string().min(1).openapi({ example: 'translator@district.org' }),
  approvedAt: z.string().datetime(),
  useCount: z.number().int().min(0).openapi({ example: 42 }),
  lastUsedAt: z.string().datetime(),
  fuzzyMatch: z.boolean().openapi({ example: false }),
  confidence: z.number().min(0).max(1).openapi({ example: 1 }),
}).openapi('TranslationMemoryEntry')

export const GlossaryTermSchema = z.object({
  id: z.string().min(1).max(64).openapi({ example: 'glossary-iep-es' }),
  term: z.string().min(1).openapi({ example: 'Individualized Education Program' }),
  locale: LocaleCodeSchema,
  translation: z.string().min(1).openapi({ example: 'Programa de Educación Individualizada' }),
  partOfSpeech: z.enum(['noun', 'verb', 'adjective', 'acronym']).openapi({ example: 'noun' }),
  domain: z.enum(['sped', 'legal', 'medical', 'general']).openapi({ example: 'sped' }),
  context: z.string().min(1).max(500).openapi({ example: 'Legal document for special education' }),
  prohibited: z.array(z.string()).optional().openapi({ description: 'Terms that must NOT be used as translation' }),
  caseSensitive: z.boolean().openapi({ example: true }),
  approvedBy: z.string().min(1),
  approvedAt: z.string().datetime(),
}).openapi('GlossaryTerm')

export type TranslationOptions = z.infer<typeof TranslationOptionsSchema>
export type TranslationResult = z.infer<typeof TranslationResultSchema>
export type ProviderHealth = z.infer<typeof ProviderHealthSchema>
export type TranslationRequest = z.infer<typeof TranslationRequestSchema>
export type TranslationJob = z.infer<typeof TranslationJobSchema>
export type TranslationMemoryEntry = z.infer<typeof TranslationMemoryEntrySchema>
export type GlossaryTerm = z.infer<typeof GlossaryTermSchema>
